import {
  AggregationType,
  TLabelConfig,
  TOTAL_CALCULATED_DATA_KEY,
} from "~/ui-rtk/constants/charts"
import Card from "~/ui-rtk/components/ui/common/Card"
import { H3, P } from "~/ui-rtk/components/ui/typography"
import pluralize from "pluralize"
import { TooltipProps } from "recharts"
import {
  NameType,
  Payload,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent"
import { formatNumber } from "~/ui-rtk/utils/format"
import { calcDelta, getLightColor } from "~/ui-rtk/utils/chart-utils"
import { sortBy } from "lodash"
import numeral from "numeral"
import React, { HTMLAttributes, PropsWithChildren, useMemo } from "react"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

import useDate from "~/ui-rtk/hooks/date"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import MarathonDataLogo from "../../svg/Logo"
const dayjs = useDate()

export type TTootlipNumberProps = {
  numeralFormat: string
  renderCell?: (
    val: number,
    dataKey: string,
    payload: Payload<ValueType, NameType>,
    labelConfig: TLabelConfig,
    ignoreDecimals?: boolean,
    keyPrefix?: string,
    excludeFromTotal?: string[],
  ) => string
}

type TChartTooltipContentProps = TooltipProps<ValueType, NameType> & {
  title?: string
  dataAggType?: AggregationType
  dataLabels: Map<string, TLabelConfig>
  totalLabelConfig?: TLabelConfig
  excludeDataKeyFromTotal?: Array<string>
  compareMode?: boolean
  isCompareMetricMain?: boolean
  highlightKey?: string
  showTotal?: boolean
  ignoreDecimals?: boolean
  isTotalCalculated?: boolean
  tooltipProps?: TTootlipNumberProps
  graphsManualSort?: boolean
  hideDates?: boolean
  blurTitle?: boolean
  hideTooltipTitle?: boolean
}

const AGG_TYPE_DATE_FORMAT = {
  [AggregationType.DAILY]: "LL",
  [AggregationType.WEEKLY]: "LL ([Week] W)",
  [AggregationType.MONTHLY]: "MMM YYYY",
}

export default function ChartTooltipContent({
  title,
  dataAggType = AggregationType.DAILY,
  label,
  payload,
  dataLabels,
  compareMode,
  highlightKey,
  excludeDataKeyFromTotal: excludeFromTotal,
  isCompareMetricMain = false,
  showTotal = true,
  ignoreDecimals = false,
  isTotalCalculated = false,
  totalLabelConfig: totalLabelConfigFromProps,
  tooltipProps,
  graphsManualSort = false,
  hideDates = false,
  blurTitle = false,
  hideTooltipTitle = false,
  children = null,
}: PropsWithChildren<TChartTooltipContentProps>) {
  const [mainPrefix, comparePrefix] = isCompareMetricMain
    ? ["compared.", ""]
    : ["", "compared."]

  const date = payload?.[0].payload[`${mainPrefix}date`]
  const compareDate = payload?.[0].payload[`${comparePrefix}date`]

  const orderedDataLabels = graphsManualSort
    ? Array.from(dataLabels.values())
    : sortBy(Array.from(dataLabels.values()), "order")

  const excludeFromTotalWithCompare = useMemo(
    () =>
      (excludeFromTotal ?? []).reduce(
        (acc, metricToExclude) => [
          ...acc,
          metricToExclude,
          `compared.${metricToExclude}`,
        ],
        [] as string[],
      ),
    [excludeFromTotal],
  )

  const totalValue = isTotalCalculated
    ? payload?.[0].payload[
        totalLabelConfigFromProps?.dataKey
          ? totalLabelConfigFromProps?.dataKey.toLowerCase()
          : TOTAL_CALCULATED_DATA_KEY
      ]
    : orderedDataLabels.reduce(
        (acc: number, { dataKey }: { dataKey: string }) => {
          if (
            excludeFromTotalWithCompare?.includes(dataKey) ||
            dataKey === TOTAL_CALCULATED_DATA_KEY
          ) {
            return acc
          }

          const value = Number(payload?.[0].payload[dataKey]) || 0
          return acc + value
        },
        0,
      )

  const compareTotalValue: number = isTotalCalculated
    ? payload?.[0].payload[
        totalLabelConfigFromProps?.dataKey
          ? `compared.${totalLabelConfigFromProps?.dataKey.toLowerCase()}`
          : `compared.${TOTAL_CALCULATED_DATA_KEY}`
      ]
    : orderedDataLabels.reduce(
        (acc: number, { dataKey }: { dataKey: string }) => {
          if (
            excludeFromTotalWithCompare?.includes(dataKey) ||
            dataKey === TOTAL_CALCULATED_DATA_KEY
          ) {
            return acc
          }

          const value = Number(payload?.[0].payload[`compared.${dataKey}`]) || 0
          return acc + value
        },
        0,
      )

  const [total, compareTotal] = isCompareMetricMain
    ? [compareTotalValue, totalValue]
    : [totalValue, compareTotalValue]

  const totalLabelConfig = useMemo(
    () =>
      totalLabelConfigFromProps ?? dataLabels.get(`${payload?.[0].dataKey}`),
    [totalLabelConfigFromProps, dataLabels, payload],
  )

  const totalDelta = calcDelta(total, compareTotal)

  const totalColorClassName = useMemo(() => {
    if (totalDelta === null) {
      return ""
    }
    return totalDelta < 0 ? "text-error" : "text-green-bright"
  }, [totalDelta])

  const renderCell = (value: ValueType, labelConfig: TLabelConfig) =>
    `${
      tooltipProps?.numeralFormat
        ? numeral(value).format(tooltipProps?.numeralFormat)
        : formatNumber(Number(value), {
            compact: labelConfig.numberType !== "currency",
            currency: labelConfig.numberType === "currency",
            decimals: ignoreDecimals ? 0 : 2,
          })
    } ${
      labelConfig.unit
        ? ` ${labelConfig.pluralizeUnit ? pluralize(labelConfig.unit, Number(value)) : labelConfig.unit}`
        : ""
    }`

  const { isEnabled: isDemoEnabled } = useDemoMode()

  const dividerStyle = { "--fallback-bc": "#101028" } as React.CSSProperties

  const content = useMemo(
    () =>
      compareMode ? (
        <>
          {!hideDates && (
            <div className="mb-2">
              <P className="text-sm text-white/60">
                {dayjs(date).format(AGG_TYPE_DATE_FORMAT[dataAggType])}
                &nbsp;&nbsp;&nbsp;
              </P>
              <P className="text-sm text-white/50">
                Compare to:{" "}
                {dayjs(compareDate).format(AGG_TYPE_DATE_FORMAT[dataAggType])}
                &nbsp;&nbsp;&nbsp;
              </P>
            </div>
          )}
          <table className="table table-zebra border-spacing-y-4 border-separate">
            <thead>
              <tr>
                <th className="p-0.5">Metric</th>
                <th className="p-0.5 text-center">Actual</th>
                <th className="p-0.5 text-center">Compare</th>
                <th className="p-0.5 text-end">Delta</th>
              </tr>
            </thead>
            <tbody>
              {orderedDataLabels.map(({ dataKey, isBrand }) => {
                const payloadItem = payload?.find(
                  ({ dataKey: itemDataKey }) => dataKey === itemDataKey,
                )
                if (!payloadItem) {
                  return null
                }
                const { value: payloadValue, color } =
                  payloadItem as unknown as {
                    value: string | number
                    color: string
                  }
                const labelConfig = dataLabels.get(`${dataKey}`)
                if (!labelConfig) {
                  return null
                }

                const [value, compareValue] = isCompareMetricMain
                  ? [
                      payload?.[0].payload[`compared.${dataKey}`] ?? 0,
                      payloadValue,
                    ]
                  : [
                      payloadValue,
                      payload?.[0].payload[`compared.${dataKey}`] ?? 0,
                    ]

                const delta = calcDelta(value, compareValue)
                let colorClassName = ""
                if (delta !== null) {
                  colorClassName =
                    delta < 0 ? "text-error" : "text-green-bright"
                }

                const tdMainStyle =
                  {} as HTMLAttributes<HTMLTableCellElement>["style"]
                const tdComparedStyle =
                  {} as HTMLAttributes<HTMLTableCellElement>["style"]

                if (color && tdMainStyle) {
                  tdMainStyle.borderLeft = `3px solid ${getLightColor(color)}`
                }
                if (color && tdComparedStyle) {
                  tdComparedStyle.borderLeft = `3px dashed ${getLightColor(color)}`
                }

                return (
                  <tr key={dataKey} className="text-sm">
                    <td
                      className={cn(
                        "p-0.5 pe-4 flex items-start justify-center gap-1",
                        isDemoEnabled && blurTitle && "blur-md",
                      )}
                    >
                      {isBrand && <MarathonDataLogo />}
                      {labelConfig.label}
                    </td>
                    <td className="py-0.5 px-2 text-start" style={tdMainStyle}>
                      {tooltipProps?.renderCell && payload
                        ? tooltipProps.renderCell(
                            value as number,
                            dataKey,
                            payload[0].payload,
                            labelConfig,
                            ignoreDecimals,
                            mainPrefix,
                            excludeFromTotalWithCompare,
                          )
                        : renderCell(value, labelConfig)}
                    </td>
                    <td
                      className="py-0.5 px-2 text-start"
                      style={tdComparedStyle}
                    >
                      {tooltipProps?.renderCell && payload
                        ? tooltipProps.renderCell(
                            compareValue as number,
                            dataKey,
                            payload[0].payload,
                            labelConfig,
                            ignoreDecimals,
                            comparePrefix,
                            excludeFromTotalWithCompare,
                          )
                        : renderCell(compareValue, labelConfig)}
                    </td>
                    <td
                      className={[colorClassName, "p-0.5 px-2 text-end"]
                        .filter(c => c)
                        .join(" ")}
                    >
                      {delta !== null
                        ? `${delta > 0 ? "+" : ""}${formatNumber(Number(delta), { decimals: 1 })}%`
                        : "-"}
                    </td>
                  </tr>
                )
              })}
              {showTotal && (
                <tr className="text-sm border-t-2 border-t-background-dark pt-0.5 mt-0.5">
                  <td className="p-0.5">
                    {totalLabelConfig?.label ?? "Total"}:
                  </td>
                  <td className="p-0.5 text-start">
                    {tooltipProps?.numeralFormat
                      ? numeral(total).format(tooltipProps?.numeralFormat)
                      : formatNumber(Number(total), {
                          compact: totalLabelConfig?.numberType !== "currency",
                          currency: totalLabelConfig?.numberType === "currency",
                          decimals: ignoreDecimals ? 0 : 1,
                        })}
                    {totalLabelConfig?.unit
                      ? ` ${totalLabelConfig?.pluralizeUnit ? pluralize(totalLabelConfig?.unit, Number(total)) : totalLabelConfig?.unit}`
                      : ""}
                  </td>
                  <td className="p-0.5 text-start">
                    {tooltipProps?.numeralFormat
                      ? numeral(compareTotal).format(
                          tooltipProps?.numeralFormat,
                        )
                      : formatNumber(Number(compareTotal), {
                          compact: totalLabelConfig?.numberType !== "currency",
                          currency: totalLabelConfig?.numberType === "currency",
                          decimals: ignoreDecimals ? 0 : 1,
                        })}
                    {totalLabelConfig?.unit
                      ? ` ${totalLabelConfig?.pluralizeUnit ? pluralize(totalLabelConfig?.unit, Number(total)) : totalLabelConfig?.unit}`
                      : ""}
                  </td>
                  <td
                    className={[totalColorClassName, "p-0.5 text-end"]
                      .filter(c => c)
                      .join(" ")}
                  >
                    {totalDelta !== null
                      ? `${totalDelta > 0 ? "+" : ""}${formatNumber(
                          Number(totalDelta),
                          {
                            decimals: ignoreDecimals ? 0 : 1,
                          },
                        )}`
                      : null}
                    %
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      ) : (
        <>
          {!hideDates && (
            <P className="text-white/50 mb-1 text-sm">
              {dayjs(date).format(AGG_TYPE_DATE_FORMAT[dataAggType])}
              &nbsp;&nbsp;&nbsp;
            </P>
          )}
          <div>
            {showTotal && (
              <div className="flex gap-2 text-sm">
                {/* <div>{totalLabelConfig?.label ?? "Total"}</div> */}
                <div className="text-xl font-bold text-left">
                  {tooltipProps?.numeralFormat
                    ? numeral(total).format(tooltipProps?.numeralFormat)
                    : formatNumber(Number(total), {
                        compact: totalLabelConfig?.numberType !== "currency",
                        currency: totalLabelConfig?.numberType === "currency",
                        decimals: ignoreDecimals ? 0 : 2,
                      })}
                  {totalLabelConfig?.unit
                    ? ` ${totalLabelConfig?.pluralizeUnit ? pluralize(totalLabelConfig?.unit, Number(total)) : totalLabelConfig?.unit}`
                    : ""}
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 mt-1">
            {orderedDataLabels.map(({ dataKey, isBrand }, idx) => {
              const payloadItem = payload?.find(
                ({ dataKey: itemDataKey }) => dataKey === itemDataKey,
              )
              if (!payloadItem) {
                return null
              }
              const { value, color } = payloadItem as unknown as {
                value: string | number
                color: string
              }
              const labelConfig = dataLabels.get(`${dataKey}`)
              if (!labelConfig) {
                return null
              }

              const divStyle = {} as HTMLAttributes<HTMLDivElement>["style"]

              if (color && divStyle) {
                divStyle.backgroundColor = `${getLightColor(color)}`
              }

              return (
                <React.Fragment key={idx}>
                  <div
                    key={dataKey}
                    className={cn(
                      `text-sm w-auto my-1 h-10 flex justify-start items-start gap-2`,
                      highlightKey === dataKey && "font-bold",
                    )}
                  >
                    <div className="w-1 h-10 rounded" style={divStyle}></div>
                    <div>
                      <div
                        className={cn(
                          "flex items-start justify-start gap-1",
                          isDemoEnabled && blurTitle && "blur-sm",
                        )}
                      >
                        {isBrand && <MarathonDataLogo />}
                        {labelConfig.label}&nbsp;&nbsp;&nbsp;
                      </div>
                      <div className="text-sm font-bold text-left">
                        {tooltipProps?.renderCell && payload
                          ? tooltipProps.renderCell(
                              value as number,
                              dataKey,
                              payload[0].payload,
                              labelConfig,
                              ignoreDecimals,
                              "",
                              excludeFromTotalWithCompare,
                            )
                          : renderCell(value, labelConfig)}
                      </div>
                    </div>
                  </div>
                  {idx % 2 ? (
                    <div
                      className="divider col-span-2 my-0.5"
                      style={dividerStyle}
                    ></div>
                  ) : null}
                </React.Fragment>
              )
            })}
          </div>
        </>
      ),
    [
      compareMode,
      hideDates,
      date,
      dataAggType,
      orderedDataLabels,
      payload,
      dataLabels,
      isCompareMetricMain,
      tooltipProps,
      showTotal,
      totalLabelConfig,
      totalColorClassName,
      totalDelta,
      ignoreDecimals,
      label,
    ],
  )

  return (
    <Card
      rounded
      className="bg-basic-black border border-basic-blue p-3 h-full"
    >
      {!hideTooltipTitle && <H3>{title}</H3>}
      {content}
      {children && <div className="mt-system-xxs">{children}</div>}
    </Card>
  )
}
